import {
  GENETICS_OPTIONS,
  PriceComparisonSchema,
  contactInsertSchema,
  getMarketAwareAddressSchema,
  personNameInputSchema,
  personSchema
} from "./chunk-HFE4TRSP.js";
import {
  ALLOWED_BUCKETS,
  priceRangeIdSchema
} from "./chunk-TBUW4BYS.js";
import {
  DELIVERY_METHOD_KINDS,
  courierSchemaWithValidation,
  deliveryMethodKindsSchema,
  pickupMethodSchema,
  shippingMethodSchema
} from "./chunk-W5JGIVWW.js";

// src/models/cannabinoid-ranges.model.ts
import { z } from "zod";
var THC_RANGE_OPTIONS = [
  { id: "more-than-30", min: 30, max: void 0 },
  { id: "28-30", min: 28, max: 30 },
  { id: "25-27", min: 25, max: 28 },
  { id: "20-24", min: 20, max: 25 },
  { id: "below-20", min: void 0, max: 20 }
];
var CBD_RANGE_OPTIONS = [
  { id: "more-than-20", min: 20, max: void 0 },
  { id: "5-20", min: 5, max: 20 },
  { id: "1-4", min: 1, max: 5 },
  { id: "below-1", min: void 0, max: 1 }
];
var thcRangeIdSchema = z.enum(
  THC_RANGE_OPTIONS.map((o) => o.id)
);
var cbdRangeIdSchema = z.enum(
  CBD_RANGE_OPTIONS.map((o) => o.id)
);

// src/models/checkout-totals.model.ts
function calculateCheckoutTotals({
  groups,
  services
}) {
  const productsAndShipping = groups.reduce(
    (sum, group) => sum + group.total,
    0
  );
  const dueAtPharmacy = groups.filter((group) => group.kind === "medication").reduce((sum, group) => sum + group.total, 0);
  return {
    combined: productsAndShipping + services,
    dueAtPharmacy
  };
}
function amountForPaymentType(totals, paymentType) {
  return paymentType === "combined" ? totals.combined : totals.combined - totals.dueAtPharmacy;
}

// src/models/clinic.model.ts
import { z as z2 } from "zod";
var clinicSchema = z2.object({
  id: z2.number(),
  externalId: z2.string(),
  email: z2.string(),
  minPrice: z2.number(),
  name: z2.string(),
  sort: z2.number(),
  website: z2.string()
});

// src/models/cross-sell.model.ts
var CROSS_SELL_PHASE1_CATEGORIES = [
  "Preparation & Grinders",
  "510er Batteries",
  "Vaporizers",
  "Accessories"
];
var CROSS_SELL_SLOT_ORDER = {
  "Preparation & Grinders": [
    "PAX-tray",
    "P4A2947",
    "7664",
    "2828",
    "PAX-Puck-press",
    "6907",
    "2112",
    "7593",
    "372458"
  ],
  "510er Batteries": [
    "GHO-001-AOZZ",
    "GHO-006-AOZZ",
    "GHO-004-AOZZ",
    "GHO-003-AOZZ",
    "GHO-005-AOZZ",
    "GHO-002-AOZZ",
    "CCELL-M4-Black",
    "CCELL-M4-Silver",
    "CCELL-M4-White"
  ],
  Vaporizers: [
    "7987",
    "GPD-001-APZZ",
    "377565-Greenstone",
    "6747",
    "6273",
    "P6D2927",
    "01-01-MM",
    "P6D2925",
    "7746",
    "7852"
  ],
  Accessories: ["7660", "7659", "395755", "7640", "7676", "7675"]
};
var CROSS_SELL_SLOT_SKUS = CROSS_SELL_PHASE1_CATEGORIES.flatMap(
  (category) => CROSS_SELL_SLOT_ORDER[category]
);

// src/models/customer-insurance.model.ts
import { z as z3 } from "zod";
var customerInsuranceStatus = [
  "member",
  "family",
  "pensioner"
];
var customerInsuranceStatusSchema = z3.enum(customerInsuranceStatus);
var customerInsuranceSchema = z3.object({
  id: z3.number(),
  name: z3.string().min(1).max(500),
  status: customerInsuranceStatusSchema,
  carrierId: z3.coerce.string().regex(/^\d{9}$/, "Must be 9 digits"),
  personalId: z3.string().regex(/^[a-zA-Z0-9]{10}$/, "Must be 10 characters (letters or digits)")
});

// src/models/db-constraints.model.ts
var MAX_PG_INTEGER = 2147483647;
var MIN_PG_INTEGER = -2147483648;
function isPgInteger(n) {
  return Number.isInteger(n) && n >= MIN_PG_INTEGER && n <= MAX_PG_INTEGER;
}

// src/models/doctor.model.ts
import { z as z4 } from "zod";
var SALUTATION = ["mister", "madame"];
var TITLE = ["dr", "dr_med", "prof"];
var SPECIALIZATION = [
  "general",
  "internal",
  "ent",
  "specialist",
  "private",
  "psychotherapy",
  "anesthesiology"
];
var salutationSchema = z4.enum(SALUTATION);
var titleSchema = z4.enum(TITLE);
var specializationSchema = z4.enum(SPECIALIZATION);
var doctorSchema = z4.object({
  id: z4.number(),
  externalId: z4.string(),
  personId: z4.number(),
  contactId: z4.number(),
  addressId: z4.number(),
  mainOfficeId: z4.number(),
  salutation: salutationSchema,
  title: titleSchema.nullish(),
  specialization: specializationSchema,
  facilityNumber: z4.coerce.number().refine(
    (num) => num === 0 || num >= 1e8 && num <= 999999999,
    "Facility number must be exactly 9 digits"
  ),
  doctorNumber: z4.coerce.number().refine(
    (num) => num === 0 || num >= 1e8 && num <= 999999999,
    "Facility number must be exactly 9 digits"
  ),
  website: z4.string().url().nullish(),
  createdAt: z4.date(),
  updatedAt: z4.date()
});

// src/models/doctor-office.model.ts
import { z as z5 } from "zod";
var doctorOfficeInsertSchema = z5.object({
  doctorId: z5.number(),
  contactId: z5.number(),
  addressId: z5.number(),
  officeType: z5.string().nullable()
});
var doctorOfficeWithContactInsertSchema = doctorOfficeInsertSchema.merge(
  contactInsertSchema.pick({ email: true, mobile: true, mobilePrefix: true }).partial()
);

// src/models/market.model.ts
var MarketNames = {
  de: "de"
};

// src/models/pagination.model.ts
import { z as z6 } from "zod";
var paginationInputSchema = z6.object({
  page: z6.number().min(0).default(0),
  pageSize: z6.number().min(1).max(500).default(10)
});

// src/models/payment.model.ts
import { z as z7 } from "zod";
var validatePaymentSchema = z7.discriminatedUnion("getBy", [
  z7.object({
    getBy: z7.literal("orderId"),
    orderId: z7.string()
  }),
  z7.object({
    getBy: z7.literal("paymentId"),
    paymentId: z7.string()
  })
]);
var devicePlatformSchema = z7.enum(["android", "ios"]);
var WEB_RETURN_PATH = "/checkout/payment/redirect-from";
var MOBILE_APP_IDS = /* @__PURE__ */ new Set(["com.dransay.app", "com.dransay.app.dev"]);
function isAllowedPaymentReturnUrl(value) {
  if (!URL.canParse(value)) {
    return false;
  }
  const url = new URL(value);
  if (url.protocol === "adyencheckout:") {
    return MOBILE_APP_IDS.has(url.hostname);
  }
  if (url.pathname !== WEB_RETURN_PATH) {
    return false;
  }
  if (url.protocol === "http:") {
    return url.hostname === "localhost";
  }
  return url.protocol === "https:" && (url.hostname === "shop.dransay.com" || url.hostname.endsWith(".jupiter.staging.dransay.me"));
}
var createPaymentSchema = z7.object({
  orderId: z7.string(),
  returnUrl: z7.string().refine(isAllowedPaymentReturnUrl, {
    message: "returnUrl is not an allowed payment return URL"
  }).transform((value) => new URL(value).href),
  platform: devicePlatformSchema.optional()
});
var getPaymentStatusSchema = z7.object({
  sessionId: z7.string(),
  sessionResult: z7.string().optional().transform((val) => val === "undefined" ? void 0 : val)
});
var stringToBooleanSchema = z7.enum(["true", "false"]).transform((value) => value === "true");
var paymentWebhookRawSchema = z7.object({
  live: z7.enum(["true", "false"]),
  notificationItems: z7.array(
    z7.object({
      NotificationRequestItem: z7.object({
        additionalData: z7.record(z7.string()).optional(),
        amount: z7.object({
          currency: z7.string(),
          value: z7.number()
        }),
        eventCode: z7.string(),
        eventDate: z7.string(),
        merchantAccountCode: z7.string(),
        merchantReference: z7.string(),
        operations: z7.array(z7.string()).optional(),
        paymentMethod: z7.string().optional(),
        pspReference: z7.string(),
        reason: z7.string(),
        success: z7.enum(["true", "false"])
      }).passthrough()
    })
  )
});
var paymentWebhookSchema = paymentWebhookRawSchema.extend({
  live: stringToBooleanSchema,
  notificationItems: paymentWebhookRawSchema.shape.notificationItems.element.extend({
    NotificationRequestItem: paymentWebhookRawSchema.shape.notificationItems.element.shape.NotificationRequestItem.extend(
      {
        success: stringToBooleanSchema
      }
    )
  }).array()
});
var PAYMENT_STATUS = [
  "initialized",
  "active",
  "canceled",
  "completed",
  "expired",
  "paymentPending",
  "refused",
  // Webhook statuses
  "authorised",
  "captured",
  "capture_failed",
  "chargeback",
  "refunded"
];
var updatePaymentStatusSchema = z7.object({
  orderId: z7.string(),
  paymentId: z7.string(),
  status: z7.enum(PAYMENT_STATUS)
});
var updatePaymentResultSchema = z7.object({
  orderId: z7.string(),
  paymentId: z7.string(),
  pspReference: z7.string(),
  paymentMethod: z7.string().optional(),
  cardLastFourDigits: z7.string().optional(),
  success: z7.boolean()
});

// src/models/procedures/customer/mutations/reorder-from-order.model.ts
import { z as z8 } from "zod";
var reorderFromOrderInputSchema = z8.object({
  orderId: z8.string().uuid(),
  forceReorder: z8.boolean().optional(),
  deliveryMethodUuid: z8.string().uuid().optional(),
  currentAddress: z8.object({
    postalCode: z8.string(),
    city: z8.string(),
    addressLineOne: z8.string().nullable().optional()
  }).optional(),
  currentDeliveryMethodKind: deliveryMethodKindsSchema.optional()
});
var reorderCartItemSchema = z8.object({
  id: z8.number(),
  quantity: z8.number(),
  crushed: z8.boolean(),
  priceComparison: PriceComparisonSchema
});
var reorderFromOrderResultSchema = z8.discriminatedUnion("status", [
  z8.object({
    status: z8.literal("not_possible"),
    reason: z8.enum([
      "pharmacy_disabled",
      "none_available",
      "no_delivery_methods"
    ])
  }),
  z8.object({
    status: z8.literal("warning"),
    cartNotEmpty: z8.boolean(),
    pharmacyChange: z8.boolean(),
    addressChange: z8.boolean(),
    methodChange: z8.boolean(),
    expressUnavailable: z8.boolean(),
    pharmacyName: z8.string(),
    address: z8.string(),
    methodKind: deliveryMethodKindsSchema,
    fromMethodKind: deliveryMethodKindsSchema.nullable()
  }),
  z8.object({
    status: z8.literal("written"),
    availableCount: z8.number(),
    unavailableCount: z8.number(),
    overCapacity: z8.boolean(),
    vendorId: z8.string(),
    deliveryMethodUuid: z8.string(),
    deliveryMethodKind: deliveryMethodKindsSchema,
    items: z8.array(reorderCartItemSchema),
    shippingAddress: z8.object({
      postalCode: z8.string(),
      city: z8.string(),
      addressLineOne: z8.string().nullable(),
      country: z8.string().nullable(),
      displayString: z8.string()
    }).nullable()
  })
]);

// src/models/procedures/customer/mutations/request-order-document-download.model.ts
import { z as z10 } from "zod";

// src/models/procedures/customer/queries/list-order-documents.model.ts
import { z as z9 } from "zod";
var orderDocumentTypeSchema = z9.enum([
  "invoice",
  "au",
  "kk",
  "apprezept",
  "befundbogen",
  "cover",
  "signed",
  "all"
]);
var listOrderDocumentsSchema = z9.object({
  orderId: z9.string()
});
var listOrderDocumentsResponseSchema = z9.object({
  types: z9.array(orderDocumentTypeSchema)
});

// src/models/procedures/customer/mutations/request-order-document-download.model.ts
var requestOrderDocumentDownloadSchema = z10.object({
  orderId: z10.string(),
  type: orderDocumentTypeSchema
});

// src/models/procedures/customer/mutations/update-customer.model.ts
import { validateMobilePhoneNumber } from "@dransay/phone-fix-test";
import { z as z11 } from "zod";
var customerUpdateDataSchema = z11.object({
  firstName: personNameInputSchema,
  lastName: personNameInputSchema,
  dateOfBirth: personSchema.shape.dateOfBirth
}).merge(contactInsertSchema.pick({ mobilePrefix: true, mobile: true })).refine((data) => Boolean(data.firstName?.trim()), {
  message: "First name is required",
  path: ["firstName"]
}).refine((data) => Boolean(data.lastName?.trim()), {
  message: "Last name is required",
  path: ["lastName"]
}).refine(
  (data) => {
    if (!data.mobile || !data.mobilePrefix) {
      return false;
    }
    const normalizedPrefix = data.mobilePrefix.startsWith("+") ? data.mobilePrefix : `+${data.mobilePrefix}`;
    const fullPhoneNumber = `${normalizedPrefix}${data.mobile}`;
    const validation = validateMobilePhoneNumber(fullPhoneNumber);
    return validation.success;
  },
  {
    message: "Phone number is required and must be valid",
    path: ["mobile"]
  }
);
var PatientDataTranslations = {
  required: "Required"
};
var gteCustomerIdentitySchema = (_market, params = PatientDataTranslations) => {
  return z11.object({
    identificationUrl: z11.string({ message: params.required })
  });
};
var getCustomerUpdateAddressSchema = (...params) => {
  const baseSchema = getMarketAwareAddressSchema(...params);
  return baseSchema.omit({
    country: true,
    countryShort: true
  }).extend({
    confirmAddressManually: baseSchema.shape.confirmAddressManually
  });
};
var customerUpdateAddressSchema = getCustomerUpdateAddressSchema().extend({
  postalCode: z11.string().trim().min(1)
});
var customerUpdateInsuranceSchema = customerInsuranceSchema.pick({
  name: true,
  status: true,
  personalId: true,
  carrierId: true
});
var addCustomerFavoriteSchema = z11.object({
  productId: z11.coerce.number().int().positive()
});
var removeCustomerFavoriteSchema = z11.object({
  productId: z11.coerce.number().int().positive()
});
var syncCustomerFavoritesSchema = z11.object({
  productIds: z11.array(z11.coerce.number().int().positive()).max(100)
});

// src/models/procedures/customer/mutations/update-id-document.model.ts
import { z as z12 } from "zod";
var updateIdDocumentSchema = z12.object({
  key: z12.string()
});

// src/models/procedures/customer/queries/get-customer-order-by-order-id.model.ts
import { z as z13 } from "zod";
var getCustomerOrderByOrderIdSchema = z13.object({
  orderId: z13.string()
});
var ORDER_STATUSES_WITH_RATING_ENABLED = [
  "approved",
  "signed",
  "sent"
];

// src/models/procedures/customer/queries/get-customer-orders.model.ts
import { z as z14 } from "zod";
var orderStatus = z14.enum([
  "pending",
  "paid",
  "shipping",
  "delivered",
  "cancelled",
  "approved",
  "declined",
  "transferred"
]);

// src/models/procedures/customer/queries/get-customer-purchase.model.ts
import { z as z15 } from "zod";
var getCustomerPurchaseSchema = z15.object({
  orderId: z15.string()
});

// src/models/procedures/customer/queries/get-order-delivery-status.model.ts
import { z as z16 } from "zod";
var getOrderDeliveryStatusSchema = z16.object({
  orderId: z16.string().min(1)
});

// src/models/procedures/customer/queries/list-my-rating-products.model.ts
import { z as z17 } from "zod";
var LIST_MY_RATING_PRODUCTS_DEFAULT_PAGE_SIZE = 10;
var LIST_MY_RATING_PRODUCTS_SORT_OPTIONS = [
  "bestRated",
  "lowestRated",
  "purchaseNewest",
  "purchaseOldest"
];
var listMyRatingProductsSortSchema = z17.enum(
  LIST_MY_RATING_PRODUCTS_SORT_OPTIONS
);
var listMyRatingProductsInputSchema = paginationInputSchema.extend({
  sort: listMyRatingProductsSortSchema.default("bestRated"),
  pageSize: z17.number().min(1).max(50).default(LIST_MY_RATING_PRODUCTS_DEFAULT_PAGE_SIZE)
});

// src/models/procedures/public/queries/check-express-courier-availability-batch.model.ts
import { z as z18 } from "zod";
var MAX_EXPRESS_COURIER_AVAILABILITY_VENDOR_IDS = 50;
var checkExpressCourierAvailabilityBatchSchema = z18.object({
  vendorIds: z18.array(z18.number().int().positive()).min(1).max(MAX_EXPRESS_COURIER_AVAILABILITY_VENDOR_IDS).describe("Vendor ids to check. Duplicates are collapsed."),
  postalCode: z18.string().regex(/^\d{5}$/, "postalCode must be exactly 5 digits")
});

// src/models/procedures/public/queries/get-nearby-vendors.model.ts
import { z as z19 } from "zod";
var MAX_DISTANCE_METERS = 5e4;
var DEFAULT_DISTANCE_METERS = 1e4;
var getNearbyVendorsSchema = z19.object({
  postalCode: z19.string(),
  countryCode: z19.string(),
  maxDistance: z19.number().min(1).max(MAX_DISTANCE_METERS).default(DEFAULT_DISTANCE_METERS)
});
var nearbyVendorSchema = z19.object({
  id: z19.number(),
  name: z19.string(),
  lat: z19.number(),
  lng: z19.number(),
  distance: z19.number(),
  deliveryMethods: z19.array(z19.string()),
  featured: z19.boolean()
});
var getNearbyVendorsResponseSchema = z19.object({
  vendors: z19.array(nearbyVendorSchema),
  actualRadius: z19.number()
});

// src/shared/schemas/sorting.ts
import { z as z20 } from "zod";
var SortOrderSchema = z20.enum(["asc", "desc"]).optional();

// src/models/procedures/public/queries/get-vendors-filtered.model.ts
import { z as z21 } from "zod";
var MAX_DISTANCE_METERS2 = 5e4;
var VendorsFilteredSortableFields = [
  "name",
  "freeFrom",
  "distance"
];
var vendorsFilteredLocationSchema = z21.object({
  postalCode: z21.string().trim().min(1),
  streetName: z21.string().trim().min(1).optional(),
  houseNr: z21.string().trim().min(1).optional(),
  city: z21.string().trim().min(1).optional(),
  streetAdditional: z21.string().trim().min(1).optional()
}).strict();
var vendorsFilteredFilterSchema = z21.object({
  deliveryMethodKinds: z21.array(z21.enum(DELIVERY_METHOD_KINDS)).optional(),
  combinedPaymentEnabled: z21.boolean().optional(),
  isTop: z21.boolean().optional(),
  maxDistance: z21.number().min(1).max(MAX_DISTANCE_METERS2).optional()
});
var vendorsFilteredSortSchema = z21.object({
  sortBy: z21.enum(VendorsFilteredSortableFields).default("name"),
  sortOrder: SortOrderSchema.default("asc")
}).optional();
var getVendorsFilteredSchema = paginationInputSchema.extend({
  pageSize: z21.number().min(1).max(1e3).default(10),
  filter: vendorsFilteredFilterSchema.optional(),
  location: vendorsFilteredLocationSchema.optional(),
  sort: vendorsFilteredSortSchema
}).superRefine((input, ctx) => {
  const sortBy = input.sort?.sortBy ?? "name";
  const needsLocation = sortBy === "distance" || input.filter?.maxDistance != null;
  if (needsLocation && input.location == null) {
    ctx.addIssue({
      code: z21.ZodIssueCode.custom,
      message: "location is required when sorting by distance or filtering by maxDistance",
      path: ["location"]
    });
  }
  const includesExpress = input.filter?.deliveryMethodKinds?.includes("express-courier");
  if (includesExpress && input.location == null) {
    ctx.addIssue({
      code: z21.ZodIssueCode.custom,
      message: "location (DeliveryAddress with postalCode) is required when filtering by express-courier",
      path: ["location"]
    });
  }
});

// src/models/procedures/storefront/queries/get-product-vendors.model.ts
import { z as z23 } from "zod";

// src/models/vendor.model.ts
import { z as z22 } from "zod";
var VENDOR_AGGREGATION_GROUP = {
  ALL: "all",
  TOP: "top"
};
var VENDOR_ID_FILTER_MESSAGE = "vendorId must be 'all', 'top', or a numeric ID";
var isValidVendorIdFilter = (value) => value === VENDOR_AGGREGATION_GROUP.ALL || value === VENDOR_AGGREGATION_GROUP.TOP || /^\d+$/.test(value);
var normalizeVendorIdFilter = (value) => {
  if (value == null || value === "") {
    return void 0;
  }
  return isValidVendorIdFilter(value) ? value : void 0;
};
var vendorIdFilterSchema = z22.string().refine(isValidVendorIdFilter, { message: VENDOR_ID_FILTER_MESSAGE }).optional();
var vendorAggregationGroups = [
  VENDOR_AGGREGATION_GROUP.TOP,
  VENDOR_AGGREGATION_GROUP.ALL
];
var vendorAggregationSchema = z22.enum(vendorAggregationGroups);
var changeItemAvailabilitySchema = z22.object({
  vendorItemId: z22.coerce.number(),
  available: z22.boolean()
});
var changeItemStockSchema = z22.object({
  vendorItemId: z22.coerce.number(),
  stock: z22.coerce.number()
});
var changeItemPriceSchema = z22.object({
  vendorItemId: z22.coerce.number(),
  price: z22.coerce.number()
});
var VendorInfoUpdateSchema = z22.object({
  name: z22.string().optional(),
  logoId: z22.number().nullable().optional(),
  owner: z22.string().optional(),
  featured: z22.boolean().optional(),
  disabled: z22.boolean().optional(),
  alternateName: z22.string().optional(),
  website: z22.string().optional(),
  description: z22.string().optional(),
  contact: z22.object({
    owner: z22.string().optional(),
    email: z22.string().email("Invalid email address").optional().or(z22.literal("")),
    phone: z22.string().optional(),
    phonePrefix: z22.string().optional()
  }).optional()
});
var VendorAddressUpdateSchema = z22.object({
  addressLineOne: z22.string().optional(),
  addressLineTwo: z22.string().optional(),
  city: z22.string().optional(),
  country: z22.string().optional(),
  countryShort: z22.string().optional(),
  postalCode: z22.string().optional(),
  province: z22.string().optional(),
  isDefaultShipping: z22.boolean().optional(),
  isDefaultDelivery: z22.boolean().optional()
});
var updateVendorSchema = z22.discriminatedUnion("type", [
  z22.object({
    type: z22.literal("info"),
    payload: VendorInfoUpdateSchema
  }),
  z22.object({
    type: z22.literal("address"),
    payload: VendorAddressUpdateSchema
  })
]);
var upsertItemsSchema = z22.object({
  rows: z22.array(
    z22.union([
      z22.object({
        ansayId: z22.string(),
        productId: z22.string().optional().nullable(),
        pzn: z22.string().optional().nullable(),
        price: z22.coerce.number().int(),
        stock: z22.coerce.number().int(),
        packageSize: z22.coerce.number().optional().nullable()
      }),
      z22.object({
        ansayId: z22.string().optional().nullable(),
        productId: z22.string(),
        pzn: z22.string().optional().nullable(),
        price: z22.coerce.number().int(),
        stock: z22.coerce.number().int(),
        packageSize: z22.coerce.number().optional().nullable()
      })
    ])
  )
});
var UPSERT_ITEMS_ERROR_CODES = {
  PRODUCTS_NOT_FOUND: "PRODUCTS_NOT_FOUND",
  STOCK_BELOW_ZERO: "STOCK_BELOW_ZERO",
  PRICE_BELOW_ZERO: "PRICE_BELOW_ZERO",
  INVALID_CATEGORY_FOR_PACKAGE_SIZE: "INVALID_CATEGORY_FOR_PACKAGE_SIZE"
};
var VendorServiceEnum = /* @__PURE__ */ ((VendorServiceEnum2) => {
  VendorServiceEnum2["MEDICAL_CANNABIS"] = "medicalCannabis";
  VendorServiceEnum2["LIFESTYLE_MEDICINE"] = "lifestyleMedicine";
  VendorServiceEnum2["WEGOVY_OZEMPIC"] = "wegovyOzempic";
  VendorServiceEnum2["OTHER_MEDICINES"] = "otherMedicines";
  return VendorServiceEnum2;
})(VendorServiceEnum || {});
var vendorServicesSchema = z22.array(z22.nativeEnum(VendorServiceEnum));
var updateVendorServicesInput = z22.object({
  medicalCannabis: z22.boolean(),
  lifestyleMedicine: z22.boolean(),
  wegovyOzempic: z22.boolean(),
  otherMedicines: z22.boolean()
});
var updateVendorPickupSchema = z22.object({
  deliveryMethod: pickupMethodSchema.omit({ id: true })
});
var updateVendorShippingSchema = z22.object({
  deliveryMethod: shippingMethodSchema
});
var updateVendorCourierSchema = z22.object({
  deliveryMethod: courierSchemaWithValidation
});
var DaysOfWeek = /* @__PURE__ */ ((DaysOfWeek2) => {
  DaysOfWeek2["MONDAY"] = "monday";
  DaysOfWeek2["TUESDAY"] = "tuesday";
  DaysOfWeek2["WEDNESDAY"] = "wednesday";
  DaysOfWeek2["THURSDAY"] = "thursday";
  DaysOfWeek2["FRIDAY"] = "friday";
  DaysOfWeek2["SATURDAY"] = "saturday";
  DaysOfWeek2["SUNDAY"] = "sunday";
  return DaysOfWeek2;
})(DaysOfWeek || {});
var updateVendorHoursSchema = z22.object({
  hours: z22.array(
    z22.object({
      dayOfWeek: z22.nativeEnum(DaysOfWeek),
      opensAt: z22.string().nullable().optional(),
      closesAt: z22.string().nullable().optional(),
      enabled: z22.boolean()
    })
  )
}).superRefine((data, ctx) => {
  data.hours.forEach((hour, index) => {
    if (!hour.enabled) {
      return;
    }
    if (!hour.opensAt) {
      ctx.addIssue({
        code: z22.ZodIssueCode.custom,
        message: "Provide a time for the opening hour",
        path: ["hours", index, "opensAt"]
      });
    }
    if (!hour.closesAt) {
      ctx.addIssue({
        code: z22.ZodIssueCode.custom,
        message: "Provide a time for the closing hour",
        path: ["hours", index, "closesAt"]
      });
    }
    const opensAt = /* @__PURE__ */ new Date(`1970-01-01T${hour.opensAt}`);
    const closesAt = /* @__PURE__ */ new Date(`1970-01-01T${hour.closesAt}`);
    if (opensAt >= closesAt) {
      ctx.addIssue({
        code: z22.ZodIssueCode.custom,
        message: "Open at must be before close at",
        path: ["hours", index, "opensAt"]
      });
      ctx.addIssue({
        code: z22.ZodIssueCode.custom,
        message: "Close at must be after open at",
        path: ["hours", index, "closesAt"]
      });
    }
  });
});

// src/models/procedures/storefront/queries/get-product-vendors.model.ts
var minimalProductVendorFilterSchema = z23.object({
  deliveryMethodKind: z23.enum(DELIVERY_METHOD_KINDS).optional(),
  id: z23.preprocess(
    (value) => normalizeVendorIdFilter(typeof value === "string" ? value : void 0),
    z23.string().optional()
  )
});
var productVendorsFilterSchema = minimalProductVendorFilterSchema.extend({
  topProducersLowPrice: z23.boolean().optional()
});
var getProductVendorsSchema = paginationInputSchema.extend({
  productId: z23.number().int().min(1).max(MAX_PG_INTEGER),
  filter: productVendorsFilterSchema.optional(),
  customerPostalCode: z23.string().optional(),
  customerCountryCode: z23.string().length(2).optional()
});

// src/models/procedures/storefront/queries/get-products.model.ts
import { z as z24 } from "zod";
var ProductsSortableFields = [
  "createdAt",
  "thc",
  "rating",
  "popularity",
  "price",
  "recommended",
  "distance"
];
var ProductsSortOrders = ["asc", "desc"];
var ProductsSortSchema = z24.object({
  sortBy: z24.enum(ProductsSortableFields).optional(),
  sortOrder: SortOrderSchema
}).optional().nullable();
var getProductsFilterSchema = () => z24.object({
  // Accept a single genetics value (legacy URLs) by coercing it into a
  // one-element array, then validate as an array for multi-select (OR).
  genetics: z24.preprocess(
    (value) => typeof value === "string" ? [value] : value,
    z24.array(z24.enum(GENETICS_OPTIONS))
  ).optional(),
  thc: z24.array(thcRangeIdSchema).optional(),
  cbd: z24.array(cbdRangeIdSchema).optional(),
  effects: z24.array(z24.string()).optional(),
  flavors: z24.array(z24.string()).optional(),
  complaints: z24.array(z24.string()).optional(),
  terpenes: z24.array(z24.string()).optional(),
  irradiation: z24.array(z24.string()).optional(),
  producers: z24.array(z24.coerce.number()).optional(),
  topProducersLowPrice: z24.boolean().optional(),
  vendorId: vendorIdFilterSchema,
  deliveryMethodKind: z24.enum(DELIVERY_METHOD_KINDS).optional(),
  category: z24.array(z24.coerce.number()).optional(),
  priceRanges: z24.array(priceRangeIdSchema).optional(),
  rating: z24.array(z24.union([z24.number().min(1).max(5), z24.literal(0)])).optional(),
  expertReview: z24.boolean().optional(),
  // Geolocation filter - find products from vendors within radius
  geo: z24.object({
    lat: z24.number().min(-90).max(90),
    lng: z24.number().min(-180).max(180),
    radiusKm: z24.number().min(0).max(500).default(50)
  }).optional()
});
var legacyCannabinoidRangeSchema = z24.object({
  min: z24.number().min(0).optional(),
  max: z24.number().min(0).optional()
});
var getProductsInputFilterSchema = () => getProductsFilterSchema().extend({
  thc: z24.union([z24.array(thcRangeIdSchema), legacyCannabinoidRangeSchema]).optional(),
  cbd: z24.union([z24.array(cbdRangeIdSchema), legacyCannabinoidRangeSchema]).optional()
});
var ProductsFilterConfigSchema = z24.object({
  thc: z24.object({
    min: z24.number(),
    max: z24.number()
  }),
  cbd: z24.object({
    min: z24.number(),
    max: z24.number()
  }),
  terpenes: z24.array(
    z24.object({
      id: z24.number(),
      name: z24.string(),
      color: z24.string().regex(
        /^(#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})|rgb\(|rgba\(|hsl\(|hsla\()/,
        "Invalid color format. Use hex (#RGB, #RRGGBB, #RRGGBBAA), rgb(), rgba(), hsl(), or hsla()"
      )
    })
  ),
  effects: z24.array(z24.string()),
  flavors: z24.array(z24.string()),
  complaints: z24.array(z24.string()),
  irradiation: z24.array(z24.string()),
  producers: z24.array(
    z24.object({
      id: z24.number(),
      name: z24.string(),
      isTopProducer: z24.boolean()
    })
  ),
  categories: z24.array(
    z24.object({
      id: z24.number(),
      name: z24.string()
    })
  ),
  priceRanges: z24.array(
    z24.object({
      id: priceRangeIdSchema,
      min: z24.number().optional(),
      max: z24.number().optional()
    })
  )
});
var LOW_PRICE_THRESHOLD_IN_CENTS = 500;
var getProductsSchema = paginationInputSchema.extend({
  sort: ProductsSortSchema,
  filter: getProductsInputFilterSchema().optional(),
  search: z24.string().optional()
});
var getCartProductsSchema = z24.object({
  ids: z24.array(z24.string()),
  vendorId: z24.string()
});

// src/models/procedures/user/queries/signed-upload-url.model.ts
import { z as z25 } from "zod";
var getSignedUploadUrlSchema = z25.object({
  fileName: z25.string(),
  fileType: z25.string(),
  bucket: z25.enum([ALLOWED_BUCKETS.USER_IDS, ALLOWED_BUCKETS.PRESCRIPTIONS])
});

// src/models/questionnaire-prefill.model.ts
import { z as z26 } from "zod";
var questionnairePrefillInputSchema = z26.object({
  complaint: z26.string().min(1)
});

// src/models/storage.extensions.model.ts
import { z as z27 } from "zod";
var IMAGE_EXTENSIONS = {
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp"
  // heif: "image/heif",
  // heic: "image/heic",
};
var DOCUMENT_EXTENSIONS = {
  pdf: "application/pdf"
};
var getFileExtension = (filename) => {
  const parts = filename.toLowerCase().split(".");
  return parts[parts.length - 1] ?? "";
};
var validateExtension = (config) => {
  const allowedExtensions = Object.keys(config);
  return z27.string().superRefine((fileName, ctx) => {
    const ext = getFileExtension(fileName);
    if (!allowedExtensions.includes(ext)) {
      ctx.addIssue({
        code: z27.ZodIssueCode.custom,
        message: `Invalid file extension. Allowed extensions: ${allowedExtensions.join(", ")}`
      });
    }
  });
};
var validateMimeType = (config) => {
  const allowedMimeTypes = Object.values(config);
  return z27.string().superRefine((mimeType, ctx) => {
    if (!allowedMimeTypes.includes(mimeType)) {
      ctx.addIssue({
        code: z27.ZodIssueCode.custom,
        message: `Invalid mime type. Allowed types: ${allowedMimeTypes.join(", ")}`
      });
      return;
    }
  });
};
var createUploadSchema = (config) => {
  return z27.object({
    fileName: validateExtension(config),
    fileType: validateMimeType(config)
  }).superRefine((data, ctx) => {
    const ext = getFileExtension(data.fileName);
    const expectedMimeType = config[ext];
    if (expectedMimeType !== data.fileType) {
      ctx.addIssue({
        code: z27.ZodIssueCode.custom,
        message: "Mime type does not match file extension",
        path: ["fileType"]
      });
    }
  });
};
var ID_DOCUMENT_MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024;
var imageUploadSchema = createUploadSchema(IMAGE_EXTENSIONS);
var documentUploadSchema = createUploadSchema(DOCUMENT_EXTENSIONS);
var getMimeTypesString = (config) => Object.values(config).join(",");
var ALLOWED_IMAGE_TYPES = getMimeTypesString(IMAGE_EXTENSIONS);
var ALLOWED_DOCUMENT_TYPES = getMimeTypesString(DOCUMENT_EXTENSIONS);

// src/models/voucher.model.ts
import { z as z28 } from "zod";
var verifyVoucherInputSchema = z28.object({
  code: z28.string().trim().min(1, "Discount code is required"),
  amount: z28.number().min(0, "Amount must be a positive number")
});

export {
  THC_RANGE_OPTIONS,
  CBD_RANGE_OPTIONS,
  thcRangeIdSchema,
  cbdRangeIdSchema,
  calculateCheckoutTotals,
  amountForPaymentType,
  clinicSchema,
  CROSS_SELL_PHASE1_CATEGORIES,
  CROSS_SELL_SLOT_ORDER,
  CROSS_SELL_SLOT_SKUS,
  customerInsuranceStatus,
  customerInsuranceStatusSchema,
  customerInsuranceSchema,
  MAX_PG_INTEGER,
  MIN_PG_INTEGER,
  isPgInteger,
  SALUTATION,
  TITLE,
  SPECIALIZATION,
  salutationSchema,
  titleSchema,
  specializationSchema,
  doctorSchema,
  doctorOfficeInsertSchema,
  doctorOfficeWithContactInsertSchema,
  MarketNames,
  paginationInputSchema,
  validatePaymentSchema,
  devicePlatformSchema,
  createPaymentSchema,
  getPaymentStatusSchema,
  paymentWebhookRawSchema,
  paymentWebhookSchema,
  reorderFromOrderInputSchema,
  reorderFromOrderResultSchema,
  orderDocumentTypeSchema,
  listOrderDocumentsSchema,
  listOrderDocumentsResponseSchema,
  requestOrderDocumentDownloadSchema,
  customerUpdateDataSchema,
  gteCustomerIdentitySchema,
  getCustomerUpdateAddressSchema,
  customerUpdateAddressSchema,
  customerUpdateInsuranceSchema,
  addCustomerFavoriteSchema,
  removeCustomerFavoriteSchema,
  syncCustomerFavoritesSchema,
  updateIdDocumentSchema,
  getCustomerOrderByOrderIdSchema,
  ORDER_STATUSES_WITH_RATING_ENABLED,
  orderStatus,
  getCustomerPurchaseSchema,
  getOrderDeliveryStatusSchema,
  LIST_MY_RATING_PRODUCTS_DEFAULT_PAGE_SIZE,
  LIST_MY_RATING_PRODUCTS_SORT_OPTIONS,
  listMyRatingProductsSortSchema,
  listMyRatingProductsInputSchema,
  MAX_EXPRESS_COURIER_AVAILABILITY_VENDOR_IDS,
  checkExpressCourierAvailabilityBatchSchema,
  getNearbyVendorsSchema,
  nearbyVendorSchema,
  getNearbyVendorsResponseSchema,
  MAX_DISTANCE_METERS2 as MAX_DISTANCE_METERS,
  VendorsFilteredSortableFields,
  vendorsFilteredLocationSchema,
  vendorsFilteredFilterSchema,
  vendorsFilteredSortSchema,
  getVendorsFilteredSchema,
  VENDOR_AGGREGATION_GROUP,
  VENDOR_ID_FILTER_MESSAGE,
  isValidVendorIdFilter,
  normalizeVendorIdFilter,
  vendorIdFilterSchema,
  vendorAggregationGroups,
  vendorAggregationSchema,
  changeItemAvailabilitySchema,
  changeItemStockSchema,
  changeItemPriceSchema,
  VendorAddressUpdateSchema,
  updateVendorSchema,
  upsertItemsSchema,
  UPSERT_ITEMS_ERROR_CODES,
  VendorServiceEnum,
  vendorServicesSchema,
  updateVendorServicesInput,
  updateVendorPickupSchema,
  updateVendorShippingSchema,
  updateVendorCourierSchema,
  DaysOfWeek,
  updateVendorHoursSchema,
  minimalProductVendorFilterSchema,
  productVendorsFilterSchema,
  getProductVendorsSchema,
  ProductsSortableFields,
  ProductsSortOrders,
  ProductsSortSchema,
  getProductsFilterSchema,
  getProductsInputFilterSchema,
  ProductsFilterConfigSchema,
  LOW_PRICE_THRESHOLD_IN_CENTS,
  getProductsSchema,
  getCartProductsSchema,
  getSignedUploadUrlSchema,
  questionnairePrefillInputSchema,
  IMAGE_EXTENSIONS,
  ID_DOCUMENT_MAX_FILE_SIZE_BYTES,
  imageUploadSchema,
  documentUploadSchema,
  ALLOWED_IMAGE_TYPES,
  ALLOWED_DOCUMENT_TYPES,
  verifyVoucherInputSchema
};
//# sourceMappingURL=chunk-LAFDARGL.js.map